import React, { useState } from 'react'
import { Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Button, Textarea, Alert, Input, Box } from '@mui/joy'

interface AdminActionDialogProps {
    open: boolean
    title: string
    description: string
    severity: 'warning' | 'danger'
    requireTyping?: boolean
    confirmationText?: string
    children?: React.ReactNode
    confirmLabel: string
    loading: boolean
    onConfirm: (reason: string) => void
    onClose: () => void
}

export default function AdminActionDialog({
    open,
    title,
    description,
    severity,
    requireTyping,
    confirmationText,
    children,
    confirmLabel,
    loading,
    onConfirm,
    onClose,
}: AdminActionDialogProps) {
    const [reason, setReason] = useState('')
    const [typedConfirmation, setTypedConfirmation] = useState('')

    const isReasonValid = reason.trim().length >= 3
    const isConfirmationValid = !requireTyping || typedConfirmation === confirmationText

    const handleClose = () => {
        if (loading) return
        setReason('')
        setTypedConfirmation('')
        onClose()
    }

    const handleConfirm = () => {
        if (!isReasonValid || !isConfirmationValid) return
        onConfirm(reason.trim())
    }

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 500 }}>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Alert color={severity} variant="soft" sx={{ mb: 2 }}>
                        {description}
                    </Alert>
                    {children}
                    <Box sx={{ mt: 2 }}>
                        <Textarea
                            placeholder="Reason for this action (required, min 3 chars)"
                            minRows={2}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={loading}
                        />
                    </Box>
                    {requireTyping && confirmationText && (
                        <Box sx={{ mt: 2 }}>
                            <Input
                                placeholder={`Type "${confirmationText}" to confirm`}
                                value={typedConfirmation}
                                onChange={(e) => setTypedConfirmation(e.target.value)}
                                disabled={loading}
                                color={typedConfirmation && typedConfirmation !== confirmationText ? 'danger' : 'neutral'}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="plain" color="neutral" onClick={handleClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="solid" color={severity} loading={loading} disabled={!isReasonValid || !isConfirmationValid} onClick={handleConfirm}>
                        {confirmLabel}
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    )
}
